import { STORAGE_KEYS } from '@/constants';
import { getUserInfo } from '@/utils/user';

// ===== ROLE ENUM =====
enum Role {
  SUPER_ADMIN = 0, // スーパー管理者 - Super Administrator
  TEMPLE_ADMIN = 1, // お寺管理者 - Temple Administrator
  BRANCH_MANAGER = 2, // 拠点管理者 - Branch Manager
  STAFF_MANAGER = 3, // 一般職員管理者 - Staff Manager
  EXPERT = 4, // 専門家 - Expert/Specialist
}

// ===== PERMISSION ACTION TYPE =====
export type PermissionAction =
  // ===== MENU LEVEL 1 =====
  | 'menu:temples'
  | 'menu:admin-accounts'
  | 'menu:maintenance'
  | 'menu:notifications'
  | 'menu:data-viewing'
  | 'menu:enterprise-configuration'
  | 'menu:overall'
  | 'menu:content'
  | 'menu:courses'
  | 'menu:reservations'
  | 'menu:devices'
  // ===== MENU LEVEL 2 =====
  // Sub-menus under "全体" - A2
  | 'menu:admin-accounts-sub'
  | 'menu:users'
  | 'menu:notifications-sub'
  | 'menu:point'
  // Sub-menus under "コンテンツ" - A2
  | 'menu:movies'
  | 'menu:video-category'
  | 'menu:copy-sutra'
  | 'menu:morning-sutra'
  | 'menu:columns'
  | 'menu:column-category'
  // Sub-menus under "講座" - A2
  | 'menu:course-schedule'
  | 'menu:course-category'
  // Sub-menus under "予約" - A2
  | 'menu:counselor-online'
  | 'menu:counselor-online-type'
  | 'menu:counselor-online-schedule'
  | 'menu:bus-timetable'
  // ===== PAGE: TEMPLES =====
  | 'page:temples:list'
  | 'page:temples:create'
  // ===== PAGE: ADMIN ACCOUNTS =====
  | 'page:admin-accounts:list'
  | 'page:admin-accounts:create'
  | 'page:admin-accounts:edit'
  | 'page:admin-accounts:detail'
  // ===== PAGE: MAINTENANCE =====
  | 'page:maintenance:list'
  | 'page:maintenance:create'
  // ===== PAGE: NOTIFICATIONS =====
  | 'page:notifications:list'
  | 'page:notifications:create'
  | 'page:notifications:edit'
  | 'page:notifications:detail'
  // ===== PAGE: USERS =====
  | 'page:users:list'
  | 'page:users:create'
  | 'page:users:edit'
  // ===== PAGE: FAMILY USERS =====
  | 'page:family-users:list'
  | 'page:family-users:create'
  | 'page:family-users:edit'
  | 'page:family-users:detail'
  // ===== PAGE: CONTENT =====
  | 'page:content:list'
  | 'page:content:create'
  // ===== PAGE: STARTUP MESSAGES =====
  | 'page:startup-messages:list'
  | 'page:startup-messages:create'
  | 'page:startup-messages:edit'
  // ===== PAGE: POINT =====
  | 'page:point:list'
  | 'page:point:create'
  // ===== PAGE: MOVIES =====
  | 'page:movies:list'
  | 'page:movies:create'
  // ===== PAGE: VIDEO CATEGORY =====
  | 'page:video-category:list'
  | 'page:video-category:create'
  // ===== PAGE: COPY SUTRA =====
  | 'page:copy-sutra:list'
  | 'page:copy-sutra:create'
  | 'page:copy-sutra:detail'
  // ===== PAGE: MORNING SUTRA =====
  | 'page:morning-sutra:list'
  | 'page:morning-sutra:create'
  // ===== PAGE: SUTRA =====
  | 'page:sutra:list'
  | 'page:sutra:detail'
  | 'page:sutra:create'
  | 'page:sutra:edit'
  // ===== PAGE: COLUMNS =====
  | 'page:columns:list'
  | 'page:columns:create'
  | 'page:columns:detail'
  | 'page:columns:edit'
  // ===== PAGE: COLUMNS CATEGORY =====
  | 'page:columns-category:list'
  | 'page:columns-category:create'
  | 'page:columns-category:detail'
  | 'page:columns-category:edit'
  // ===== PAGE: COURSES =====
  | 'page:courses:list'
  | 'page:courses:create'
  // ===== PAGE: COURSE PARTICIPANT =====
  | 'page:course-participant:list'
  // ===== PAGE: COURSE CATEGORY =====
  | 'page:course-category:list'
  | 'page:course-category:create'
  // ===== PAGE: COUNSELOR ONLINE =====
  | 'page:counselor-online:list'
  | 'page:counselor-online:create'
  // ===== PAGE: COUNSELOR ONLINE TYPE =====
  | 'page:counselor-online-type:list'
  | 'page:counselor-online-type:create'
  // ===== PAGE: COUNSELOR ONLINE SCHEDULE =====
  | 'page:counselor-online-schedule:setting'
  | 'page:counselor-online-schedule:detail'
  // ===== PAGE: BUS TIMETABLE =====
  | 'page:bus-timetable:list'
  | 'page:bus-timetable:create'
  // ===== PAGE: DEVICES =====
  | 'page:devices:list'
  | 'page:devices:create';

// ===== PERMISSION MAP TYPE =====
type PermissionMap = {
  [key in PermissionAction]: Role[];
};

// ===== PERMISSION MAP =====
const permissionMap: PermissionMap = {
  // ===== MENU LEVEL 1 =====
  'menu:temples': [Role.SUPER_ADMIN],
  'menu:admin-accounts': [Role.SUPER_ADMIN],
  'menu:maintenance': [Role.SUPER_ADMIN],
  'menu:notifications': [Role.SUPER_ADMIN],
  'menu:data-viewing': [Role.SUPER_ADMIN],
  'menu:enterprise-configuration': [Role.SUPER_ADMIN],
  'menu:overall': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:content': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:courses': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:reservations': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
    Role.EXPERT,
  ],
  'menu:devices': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],

  // ===== MENU LEVEL 2 =====
  // Sub-menus under "全体" - A2
  'menu:admin-accounts-sub': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'menu:users': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:notifications-sub': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:point': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  // Sub-menus under "コンテンツ" - A2
  'menu:movies': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:video-category': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:copy-sutra': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:morning-sutra': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:columns': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'menu:column-category': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  // Sub-menus under "講座" - A2
  'menu:course-schedule': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:course-category': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  // Sub-menus under "予約" - A2
  'menu:counselor-online': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:counselor-online-type': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'menu:counselor-online-schedule': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
    Role.EXPERT,
  ],
  'menu:bus-timetable': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: TEMPLES =====
  'page:temples:list': [Role.SUPER_ADMIN],
  'page:temples:create': [Role.SUPER_ADMIN],

  // ===== PAGE: ADMIN ACCOUNTS =====
  'page:admin-accounts:list': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:admin-accounts:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:admin-accounts:edit': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:admin-accounts:detail': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: MAINTENANCE =====
  'page:maintenance:list': [Role.SUPER_ADMIN],
  'page:maintenance:create': [Role.SUPER_ADMIN],

  // ===== PAGE: NOTIFICATIONS =====
  'page:notifications:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:notifications:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:notifications:edit': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:notifications:detail': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: USERS =====
  'page:users:list': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'page:users:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:users:edit': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],

  // ===== PAGE: FAMILY USERS =====
  'page:family-users:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:family-users:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:family-users:edit': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:family-users:detail': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: CONTENT =====
  'page:content:list': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:content:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: STARTUP MESSAGES =====
  'page:startup-messages:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:startup-messages:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:startup-messages:edit': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  // ===== PAGE: POINT =====
  'page:point:list': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'page:point:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN],

  // ===== PAGE: MOVIES =====
  'page:movies:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:movies:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: VIDEO CATEGORY =====
  'page:video-category:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:video-category:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: COPY SUTRA =====
  'page:copy-sutra:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:copy-sutra:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:copy-sutra:detail': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: MORNING SUTRA =====
  'page:morning-sutra:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:morning-sutra:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: SUTRA =====
  'page:sutra:list': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER, Role.STAFF_MANAGER],
  'page:sutra:detail': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:sutra:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],
  'page:sutra:edit': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: COLUMNS =====
  'page:columns:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns:detail': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns:edit': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: COLUMNS CATEGORY =====
  'page:columns-category:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns-category:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns-category:detail': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:columns-category:edit': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: COURSES =====
  'page:courses:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:courses:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: COURSE PARTICIPANT =====
  'page:course-participant:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: COURSE CATEGORY =====
  'page:course-category:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:course-category:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN, Role.BRANCH_MANAGER],

  // ===== PAGE: COUNSELOR ONLINE =====
  'page:counselor-online:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:counselor-online:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN],

  // ===== PAGE: COUNSELOR ONLINE TYPE =====
  'page:counselor-online-type:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:counselor-online-type:create': [Role.SUPER_ADMIN, Role.TEMPLE_ADMIN],

  // ===== PAGE: COUNSELOR ONLINE SCHEDULE =====
  'page:counselor-online-schedule:setting': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
  ],
  'page:counselor-online-schedule:detail': [Role.STAFF_MANAGER, Role.EXPERT],

  // ===== PAGE: BUS TIMETABLE =====
  'page:bus-timetable:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:bus-timetable:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],

  // ===== PAGE: DEVICES =====
  'page:devices:list': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
  'page:devices:create': [
    Role.SUPER_ADMIN,
    Role.TEMPLE_ADMIN,
    Role.BRANCH_MANAGER,
    Role.STAFF_MANAGER,
  ],
};

const getUserRole = (): Role => {
  const user = getUserInfo();
  const roleNum = Number(user?.role);
  return isNaN(roleNum) ? Role.SUPER_ADMIN : (roleNum as Role);
};

// ===== PERMISSION CHECKERS =====
const hasRolePermission = (action: PermissionAction): boolean => {
  const role = getUserRole();
  return !!permissionMap[action]?.includes(role);
};

const withRolePermission = (action: PermissionAction, execute: () => void) => {
  if (hasRolePermission(action)) {
    execute();
  }
};

const isSuperAdmin = (): boolean => getUserRole() === Role.SUPER_ADMIN;
const getMenuType = (): string => localStorage.getItem(STORAGE_KEYS.MENU_TYPE) || '';
const canAccessMenuA1 = (): boolean => isSuperAdmin() && getMenuType() === 'A1';
const canAccessMenuA2 = (): boolean => getMenuType() === 'A2' || !getMenuType();

export { hasRolePermission, withRolePermission, isSuperAdmin, canAccessMenuA1, canAccessMenuA2 };

